import { FC, memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  IconButton,
  Tab,
  TabList,
  Tabs,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FiPrinter } from 'react-icons/fi';
import { DataObjectStatus, accountApi, publicApi } from '@netiva/classifieds-api';
import { useDebounce, useLocalStorageRedux } from '@netiva/classifieds-common';
import {
  Alert,
  ConfirmationDialog,
  GridIcon,
  ListIcon,
  Loader,
  OpenInNewIcon,
  Pagination,
} from '@netiva/classifieds-ui';

import { RouterLink } from '@/components/global';
import { normalizeProductGroupName, useContracts, useMatomoTagManager, usePagination } from '@/hooks';
import { ProductGroups } from '@/constants';
import { canCreate } from '../Products/utils';
import { routes } from '@/lib/routes';
import { useAppSelector } from '@/store';
import { AdGrid, AdList, Filter, UneditableModal } from './components';
import { AdListItem, AdListItemAction, AdListLayoutType, FilterStatus } from './types';
import { CommonStepKeys } from '../Ad/constants';
import { useAdListSearchParams } from './hooks';
import { getDataObjectStatus, getFilterCounts } from './utils';
import { CreateModal, UpgradeModal } from '../Products/components';

const PageSize = 20;
const EMPTY_COUNTS = {};

export const MyAdsPage: FC = memo(function MyAdsPage() {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const [isStatusChangeHandled, setIsStatusChangeHandled] = useState(false);
  const [searchParams, setSearchParams] = useAdListSearchParams();

  const { contracts, products } = useContracts();
  const upgradeModalDisclosure = useDisclosure();
  const createModalDisclosure = useDisclosure();

  const { buildMatomoTrackingAttributes, trackMatomoEvent } = useMatomoTagManager();

  const { accountId, hasMemberContract } = useAppSelector((state) => state.account);

  publicApi.useGetConfigurationPlatforms({});
  const { platforms } = useAppSelector((state) => state.global);
  const printablePlatforms = useMemo(() => platforms.filter((p) => p.allowPrintList), [platforms]);

  const [layoutType, setLayoutType] = useLocalStorageRedux<AdListLayoutType>('layoutType', 'list');

  const debouncedSearchParams = useDebounce(searchParams, 200);

  const statusTabs = useMemo<{ status: FilterStatus; label: string }[]>(
    () => [
      { status: 'active', label: t('ad.status.active') },
      { status: 'draft', label: t('ad.status.draft') },
      { status: 'inactive', label: t('ad.status.disabled') },
    ],
    [t]
  );
  const statusTabIndex = useMemo(() => {
    const index = statusTabs.findIndex((x) => x.status === searchParams.status);
    return index < 0 ? 0 : index;
  }, [searchParams.status, statusTabs]);
  const statusParam = useMemo<DataObjectStatus[]>(
    () => getDataObjectStatus(searchParams.status || 'active'),
    [searchParams]
  );

  const {
    data: adList,
    isLoading: adListLoading,
    isFetching: adListFetching,
    refetch: refetchAdList,
  } = accountApi.useGetDataObjects({
    status: statusParam,
    filter: debouncedSearchParams.query,
    platform: debouncedSearchParams.platform !== '' ? debouncedSearchParams.platform : undefined,
    topListed: debouncedSearchParams.topListed,
    pageIndex: debouncedSearchParams.pageIndex,
    categoryId: debouncedSearchParams.categoryId || undefined,
    pageSize: PageSize,
  });
  const items = useMemo(() => adList?.items || [], [adList?.items]);
  const { data: countsData, isLoading: isLoadingCounts } = accountApi.useDataObjectCounts({});
  const counts = useMemo(() => getFilterCounts(countsData?.counts), [countsData?.counts]);
  const [activateDataObject, { isLoading: isLoadingActivate }] = accountApi.useActivateDataObject();
  const [deactivateDataObject, { isLoading: isLoadingDeactivate }] = accountApi.useDeactivateDataObject();
  const [deleteDataObject, { isLoading: isLoadingDelete }] = accountApi.useDeleteDataObject();
  const [duplicateDataObject, { isLoading: isLoadingDuplicate }] = accountApi.useDuplicateDataObject();
  const [setTopListing, { isLoading: isLoadingTopListing }] = accountApi.useSetTopListing();

  const hasTopListingSlots = !!adList && adList.totalTopListingSlots > adList.usedTopListingSlots;
  const areAllTopListingSlotsUsed = !!adList && adList.totalTopListingSlots <= adList.usedTopListingSlots;
  const totalPages = Math.ceil((adList?.totalCount || 0) / PageSize);
  const paginationProps = usePagination({ totalPages });
  const categoryCounts = adList?.categoryCounts ?? EMPTY_COUNTS;

  const selectedContract = useMemo(() => {
    if (!adList?.topListingProductId) return undefined;

    return contracts.find(
      (contract) => contract.productId === adList.topListingProductId && contract.status !== 'Suspended'
    );
  }, [contracts, adList?.topListingProductId]);

  const visibleContracts = useMemo(
    () =>
      contracts.filter((c) => {
        return (
          !c.product?.isHidden &&
          c.product?.group &&
          normalizeProductGroupName(c.product.group).startsWith(normalizeProductGroupName(ProductGroups.MemberPrefix))
        );
      }),
    [contracts]
  );

  const activeContracts = useMemo(
    () => visibleContracts.filter((x) => x.status !== 'Suspended' && (!x.endDate || new Date(x.endDate) > new Date())),
    [visibleContracts]
  );

  const creatableProducts = useMemo(
    () => products.filter((p) => canCreate(p, activeContracts)),
    [products, activeContracts]
  );

  const [selectedObject, setSelectedObject] = useState<AdListItem>();
  const uneditableModal = useDisclosure();
  const pendingTopListingItemId = useRef<number | undefined>(undefined);

  const hasTrackedInitialTabView = useRef(false);

  const [deletingObjectId, setDeletingObjectId] = useState<number>();
  const {
    isOpen: isDeleteConfirmationOpen,
    onOpen: openDeleteConfirmation,
    onClose: closeDeleteConfirmation,
  } = useDisclosure();

  const handleDeleteConfirmation = async () => {
    if (deletingObjectId) {
      await deleteDataObject({ id: deletingObjectId });
      setDeletingObjectId(undefined);
      closeDeleteConfirmation();
    }
  };
  const handleDeleteCancel = () => {
    setDeletingObjectId(undefined);
    closeDeleteConfirmation();
  };

  const [stoppingObjectId, setStoppingObjectId] = useState<number>();
  const {
    isOpen: isStopConfirmationOpen,
    onOpen: openStopConfirmation,
    onClose: closeStopConfirmation,
  } = useDisclosure();

  const handleStopConfirm = async () => {
    if (stoppingObjectId) {
      await deactivateDataObject({ id: stoppingObjectId });
      setStoppingObjectId(undefined);
      closeStopConfirmation();
    }
  };

  const handleStopCancel = () => {
    setStoppingObjectId(undefined);
    closeStopConfirmation();
  };

  const setTopListingWrapper = async (id: number, active: boolean) => {
    try {
      await setTopListing({ id, active }).unwrap();
      toast({
        status: 'success',
        description: active ? t('myads.actions.enableTopListingSuccess') : t('myads.actions.disableTopListingSuccess'),
      });
    } catch (err) {
      console.error(err);
      toast({
        status: 'error',
        description: active ? t('myads.actions.enableTopListingError') : t('myads.actions.disableTopListingError'),
      });
    }
  };

  const handleAction = async (item: AdListItem, action: AdListItemAction) => {
    switch (action) {
      case 'edit':
        navigate(routes.ad(item.id, CommonStepKeys.edit));
        break;

      case 'delete':
        setDeletingObjectId(item.id);
        openDeleteConfirmation();
        break;

      case 'disable':
        deactivateDataObject({ id: item.id });
        break;

      case 'stop':
        setStoppingObjectId(item.id);
        openStopConfirmation();
        break;

      case 'duplicate':
        {
          const result = await duplicateDataObject({ id: item.id }).unwrap();
          navigate(routes.ad(result.id, CommonStepKeys.edit));
        }
        break;

      case 'enable':
        activateDataObject({ id: item.id });
        break;

      case 'enableTopListing':
        if (!hasMemberContract) break;
        if (hasTopListingSlots) {
          await setTopListingWrapper(item.id, true);
          trackMatomoEvent({
            trackingCategory: 'Interaction_PI-MyListing',
            trackingAction: 'Activate_TopListing',
          });
        } else if (selectedContract) {
          pendingTopListingItemId.current = item.id;
          upgradeModalDisclosure.onOpen();
          trackMatomoEvent({
            trackingCategory: 'Interaction_PI-MyListing',
            trackingAction: 'Click_TopListing',
            trackingName: `Click_TopListing_UpgradeProduct`,
          });
        } else if (creatableProducts.length > 0) {
          pendingTopListingItemId.current = item.id;
          createModalDisclosure.onOpen();
          trackMatomoEvent({
            trackingCategory: 'Interaction_PI-MyListing',
            trackingAction: 'Click_TopListing',
            trackingName: `Click_TopListing_BookNewProduct`,
          });
        }
        break;

      case 'disableTopListing':
        if (!hasMemberContract) break;
        setTopListingWrapper(item.id, false);
        break;

      case 'extendDuration':
        navigate(routes.ad(item.id, 'publishing'));
        break;

      case 'showUneditableModal':
        setSelectedObject(item);
        uneditableModal.onOpen();
        break;
    }
  };

  const handleStatusTabChange = (tabIndex: number) => {
    let status: FilterStatus = '';
    switch (tabIndex) {
      case 0:
        status = 'active';
        break;
      case 1:
        status = 'draft';
        break;
      case 2:
        status = 'inactive';
        break;
    }
    if (searchParams.status !== status) {
      setSearchParams({ ...searchParams, status });
    }
  };

  const handleCreateSuccess = async () => {
    if (pendingTopListingItemId.current !== undefined) {
      await setTopListingWrapper(pendingTopListingItemId.current, true);
      pendingTopListingItemId.current = undefined;
    }
    refetchAdList();
    trackMatomoEvent({
      trackingCategory: 'Interaction_PI-MyListings',
      trackingAction: 'Book_TopListing',
      trackingName: 'Book_TopListing',
    });
  };

  const handleUpgradeSuccess = async () => {
    if (pendingTopListingItemId.current !== undefined) {
      await setTopListingWrapper(pendingTopListingItemId.current, true);
      pendingTopListingItemId.current = undefined;
    }
    refetchAdList();
    trackMatomoEvent({
      trackingCategory: 'Interaction_PI-MyListing',
      trackingAction: 'Activate_TopListing',
    });
    trackMatomoEvent({
      trackingCategory: 'Interaction_PI-MyListings',
      trackingAction: 'Book_TopListing',
      trackingName: 'Book_TopListing',
    });
  };

  useEffect(() => {
    if (isLoadingCounts || searchParams.status !== 'active' || isStatusChangeHandled) {
      return;
    }
    if (!counts['active']) {
      if (counts['draft']) {
        setSearchParams({ ...searchParams, status: 'draft' }, true);
      } else if (counts['inactive']) {
        setSearchParams({ ...searchParams, status: 'inactive' }, true);
      } else {
        setSearchParams({ ...searchParams, status: 'active' }, true);
      }
    }
    setIsStatusChangeHandled(true);
  }, [counts, isLoadingCounts, isStatusChangeHandled, searchParams, setSearchParams]);

  // track initial tab view
  useEffect(() => {
    if (hasTrackedInitialTabView.current) return;

    const activeTab = statusTabs[statusTabIndex];
    if (!activeTab) return;

    trackMatomoEvent({
      trackingCategory: 'Interaction_PI-MyListing',
      trackingAction: 'View_MyListings',
      trackingName: `View_MyListings_${activeTab.status}`,
    });

    hasTrackedInitialTabView.current = true;
  }, [hasTrackedInitialTabView, statusTabs, statusTabIndex, trackMatomoEvent]);

  const hasFilter =
    searchParams.platform ||
    searchParams.categoryId ||
    searchParams.query ||
    searchParams.topListed ||
    searchParams.status !== 'active';

  const isLoading =
    adListLoading ||
    adListFetching ||
    isLoadingCounts ||
    isLoadingActivate ||
    isLoadingDeactivate ||
    isLoadingDelete ||
    isLoadingDuplicate ||
    isLoadingTopListing;

  const ListComponent = layoutType === 'grid' ? AdGrid : AdList;

  return (
    <Box m={-4} bg="neutral.50">
      <Tabs isLazy size="lg" index={statusTabIndex} onChange={handleStatusTabChange}>
        <TabList bg="white" p={4}>
          {statusTabs.map((tab, i) => {
            const matomoTrackingProps = buildMatomoTrackingAttributes({
              trackingCategory: 'Interaction_PI-MyListing',
              trackingAction: 'View_MyListings',
              trackingName: `View_MyListings_${tab.status}`,
            });
            return (
              <Tab key={i} {...matomoTrackingProps}>
                {tab.label}
                {!isLoadingCounts && (
                  <Badge fontSize="md" ml={2}>
                    {counts[tab.status] || 0}
                  </Badge>
                )}
              </Tab>
            );
          })}
        </TabList>
      </Tabs>

      <Box bg="white" p={4}>
        <Filter categoryCounts={categoryCounts} />
      </Box>
      <Divider />

      {hasMemberContract && !!adList?.totalTopListingSlots && searchParams.status === 'active' && (
        <>
          <Button
            variant="unstyled"
            w="full"
            bg="yellow.50"
            p={4}
            borderRadius={0}
            textAlign="left"
            onClick={upgradeModalDisclosure.onOpen}
            height="auto"
            minH="unset"
            fontWeight="normal"
            fontSize="sm"
          >
            <span>
              {t('myads.availableTopListings', {
                available: adList.totalTopListingSlots - adList.usedTopListingSlots,
                total: adList.totalTopListingSlots,
              })}
              <OpenInNewIcon ml="2" mb="1" boxSize="4" />
            </span>
          </Button>
          <Divider />
        </>
      )}

      <Flex align="center" justify="space-between" gap={2} px={4} pt={2}>
        <Text fontSize="sm">{t('myads.listTitle', { count: adList?.totalCount || 0 })}</Text>
        <Flex>
          <IconButton
            icon={<ListIcon boxSize={6} />}
            variant={layoutType === 'list' ? 'iconButtonActive' : 'iconButton'}
            aria-label={'List'}
            onClick={() => setLayoutType('list')}
            ml={1}
          />
          <IconButton
            icon={<GridIcon boxSize={6} />}
            variant={layoutType === 'grid' ? 'iconButtonActive' : 'iconButton'}
            aria-label={'Grid'}
            onClick={() => setLayoutType('grid')}
          />
        </Flex>
      </Flex>

      {!isLoading && items.length === 0 && (
        <Alert status="info">{hasFilter ? t('myads.emptyFiltered') : t('myads.empty')}</Alert>
      )}

      <Box position="relative" minHeight="200px" p={4}>
        <Loader isLoading={isLoading} showOverlay />
        <ListComponent
          items={items}
          onActionExecuted={handleAction}
          showPlatform={platforms.length > 1}
          tabStatus={statusTabs[statusTabIndex].status}
          hasMemberContract={hasMemberContract}
        />
      </Box>

      <Flex mt={4} justify="center" align="center" p={4}>
        <Pagination {...paginationProps} />
      </Flex>

      {accountId && printablePlatforms.length > 0 && (
        <>
          <Divider />
          <Box bg="white" p={4}>
            {printablePlatforms.map((platform) => {
              const matomoTrackingProps = buildMatomoTrackingAttributes({
                trackingCategory: 'Interaction_PI-MyListing',
                trackingAction: 'Print_Listings',
                trackingName: `Print_Listings_${platform.name}`,
              });
              return (
                <RouterLink
                  key={platform.key}
                  to={routes.myAdsPrint(accountId, platform.key, i18n.language)}
                  target="_blank"
                  {...matomoTrackingProps}
                >
                  <Flex gap={2} align="center" my={2}>
                    <Icon as={FiPrinter} boxSize={6} />
                    <Text>{t('myads.printPlatformList', { platform: platform.key })}</Text>
                  </Flex>
                </RouterLink>
              );
            })}
          </Box>
        </>
      )}

      <ConfirmationDialog
        isOpen={isDeleteConfirmationOpen}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirmation}
        cancelText={t('ui.buttons.cancel')}
        okText={t('ui.buttons.delete')}
        title={t('myads.actions.deleteConfirmation.title')}
        message={t('myads.actions.deleteConfirmation.message')}
      />

      <ConfirmationDialog
        isOpen={isStopConfirmationOpen}
        onCancel={handleStopCancel}
        onConfirm={handleStopConfirm}
        cancelText={t('ui.buttons.cancel')}
        okText={t('ui.buttons.stop')}
        title={t('myads.actions.stopConfirmation.title')}
        message={t('myads.actions.stopConfirmation.message')}
      />

      <UneditableModal item={selectedObject} {...uneditableModal} />
      <CreateModal {...createModalDisclosure} products={creatableProducts} onSuccess={handleCreateSuccess} />
      <UpgradeModal
        {...upgradeModalDisclosure}
        areAllTopListingSlotsUsed={areAllTopListingSlotsUsed}
        contract={selectedContract}
        products={products}
        onSuccess={handleUpgradeSuccess}
      />
    </Box>
  );
});
